import React from 'react';
import {
   FormControl,
   FormControlLabel,
   FormHelperText,
   Radio,
   RadioProps,
   Typography,
} from '@mui/material';
import {readOnlyContext} from 'common/context/ReadOnlyContext';
import {useController} from 'react-hook-form';
import {useRegisterLabel} from 'common/context/FormContext';

type Props = {
   name: string;
   value?: any;
   label?: string;
} & RadioProps;

type RadioButtonProps = Props & {
   error?: string | boolean;
};

const RadioButton = (props: RadioButtonProps) => {
   const {label, name, value, disabled, className, onChange, error, ...otherProps} = props;
   const readOnly = React.useContext(readOnlyContext);

   return (
      <FormControl fullWidth required={props.required} className={className}>
         <FormControlLabel
            disabled={disabled || readOnly}
            control={<Radio {...otherProps} name={name} value={value} onChange={onChange} />}
            label={<Typography variant="body2">{label}</Typography>}
         />
         {error ? <FormHelperText>{error}</FormHelperText> : null}
      </FormControl>
   );
};

const HookFormRadioButton = ({name, value, ...restProps}: Props) => {
   // const [field, meta, helpers] = useField({name, type: 'checkbox'});

   const methods = useController({
      name,
      // rules: {required: true},
      // defaultValue: '',
   });
   useRegisterLabel(name, restProps.placeholder || (restProps.label as string));

   // console.debug('HookFormRadioButton', methods);

   const {
      field: {onChange: fieldOnChange, onBlur, ref, value: checked},
      fieldState: {error: _error},
   } = methods;

   const error = _error?.message;

   const isBool = typeof value === 'boolean';
   const _onChange = React.useCallback(
      (ev) => {
         let e: any = ev;
         if (isBool) {
            e = {
               ...ev,
               target: {
                  ...ev.target,
                  name: ev.target.name,
                  value: ev.target.value === 'true',
               },
            };
         }

         fieldOnChange(e);
      },
      [isBool, fieldOnChange]
   );

   const val = value ?? true;

   return (
      <RadioButton
         onChange={_onChange}
         error={error}
         value={val}
         checked={checked === val}
         onBlur={onBlur} // notify when input is touched/blur
         // value={value} // input value
         name={name} // send down the input name
         inputRef={ref}
         {...restProps}
      />
   );
};

export default React.memo(HookFormRadioButton);
